import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ScSession, ScSlot } from "../../singles/useSinglesSession.ts";
import type { ScChain } from "../../singles/useSinglesChain.ts";
import type { ScReserve, ScTransaction } from "../../singles/useSinglesReserve.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import { BankToGoalCta } from "../../components/sika-extras/BankToGoalCta.tsx";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import { countryFromCode, fmtOdds } from "../football/_fpkit.tsx";

export type SinglesView = "today" | "activity" | "reserve";
type SelectionFilter = "all" | "pending" | "settled";

export function singlesDate(value: string | null | undefined, time = true): string {
  if (!value) return "Not recorded";
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (!Number.isFinite(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Accra",
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(time ? ({ hour: "2-digit", minute: "2-digit" } as const) : {}),
  }).format(date);
}

export function singlesMoney(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value)
    ? `GH₵${value.toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
}

export function singlesStatus(status: string | null | undefined): string {
  const labels: Record<string, string> = {
    scanning: "Scanning",
    rest_day: "Rest day",
    slots_ready: "Published",
    live: "In progress",
    completed: "Completed",
    no_fixtures: "No qualifying selection",
    active: "Active",
    settled_win: "Settled win",
    settled_loss: "Settled loss",
    busted: "Busted",
    skipped: "Skipped",
    paused: "Paused",
    depleted: "Depleted",
    pending: "Pending",
    won: "Won",
    lost: "Lost",
    void: "Void",
  };
  return status ? (labels[status] ?? status.replaceAll("_", " ")) : "Awaiting session";
}

export function selectionState(slot: ScSlot, now = Date.now()): string {
  if (slot.outcome && slot.outcome !== "pending") return singlesStatus(slot.outcome);
  const kickoff = Date.parse(slot.kickoff_at ?? "");
  return Number.isFinite(kickoff) && kickoff > now ? "Upcoming" : "Awaiting result";
}

export function SinglesWorkspaceNav({ active }: { active: SinglesView }): JSX.Element {
  const [params] = useSearchParams();
  const href = (view: SinglesView) => {
    const next = new URLSearchParams(params);
    if (view === "today") next.delete("view");
    else next.set("view", view);
    return { pathname: "/singles", search: next.toString() };
  };
  return (
    <nav className="pb-workspace-nav singles-workspace-nav" aria-label="Singles Chain workspace">
      <Link to={href("today")} aria-current={active === "today" ? "page" : undefined}>
        Today's board
      </Link>
      <Link to={href("activity")} aria-current={active === "activity" ? "page" : undefined}>
        Chain activity
      </Link>
      <Link to={href("reserve")} aria-current={active === "reserve" ? "page" : undefined}>
        Reserve &amp; history
      </Link>
      <span>FOOTBALL / SINGLES CHAIN</span>
    </nav>
  );
}

export function SinglesEmpty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="singles-empty">
      <span className="singles-empty-mark" aria-hidden="true">
        <Icon name="singles" size={20} />
      </span>
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}

function SelectionEvidence({ slot }: { slot: ScSlot }): JSX.Element {
  const evidence = slot.public_case;
  return (
    <details className="singles-evidence">
      <summary>
        Selection details <Icon name="chev" size={12} />
      </summary>
      <div className="singles-evidence-body">
        {evidence ? (
          <>
            <p className="singles-evidence-thesis">{evidence.why}</p>
            <div className="singles-evidence-results">
              {[
                { name: slot.home_team ?? "Home team", results: evidence.home_recent },
                { name: slot.away_team ?? "Away team", results: evidence.away_recent },
              ].map(({ name, results }) => (
                <section key={name}>
                  <h4>{name} · recent results</h4>
                  {results.length ? (
                    <ul>
                      {results.map((result, index) => (
                        <li key={`${index}-${result}`}>{result}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>Recent results not recorded.</p>
                  )}
                </section>
              ))}
            </div>
            <dl className="singles-detail-facts">
              <div>
                <dt>Evidence source</dt>
                <dd>{evidence.source || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Evidence through</dt>
                <dd>{singlesDate(evidence.evidence_through, false)}</dd>
              </div>
              <div>
                <dt>Quote source</dt>
                <dd>{evidence.quote_source || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Quote recorded</dt>
                <dd>{singlesDate(evidence.quoted_at)}</dd>
              </div>
            </dl>
            {evidence.caveat && (
              <p className="singles-evidence-caveat">
                <Icon name="info" size={13} />
                {evidence.caveat}
              </p>
            )}
          </>
        ) : (
          <p>
            Detailed source evidence was not retained with this publication. The recorded assessment
            alone does not establish a strong selection.
          </p>
        )}
        <div className="singles-evidence-footnote">
          <span>
            {slot.reveal_at
              ? `Published ${singlesDate(slot.reveal_at)}`
              : "Publication time not recorded"}
          </span>
          <span>{slot.simulated ? "Paper tracking" : "Personal tracking"} · Accra time</span>
        </div>
        <p className="singles-quote-note">
          The recorded price is a reference. Check the available market and price before acting.
        </p>
      </div>
    </details>
  );
}

export function SinglesSelectionRow({
  slot,
  index,
  now,
}: {
  slot: ScSlot;
  index: number;
  now?: number;
}): JSX.Element {
  const home = slot.home_team || "Home team";
  const away = slot.away_team || "Away team";
  const country = countryFromCode(slot.country_code);
  const quotedOdds =
    typeof slot.odds === "number" && Number.isFinite(slot.odds) && slot.odds > 1 ? slot.odds : null;
  const market = slot.market ? marketLabel(slot.market) : "Market not recorded";
  const pick = slot.selection
    ? selectionLabel(slot.market ?? "", slot.selection, { home_team: home, away_team: away })
    : "Selection not recorded";
  return (
    <div
      className={`singles-pick${slot.outcome === "won" ? " is-won" : slot.outcome === "lost" ? " is-lost" : ""}`}
    >
      <article className="pb-publication-row" aria-label={`${home} vs ${away}`}>
        <span className="pb-ledger-number">{String(index + 1).padStart(2, "0")}</span>
        <div className="pb-ledger-fixture">
          <strong>
            {home}
            <span className="singles-vs"> vs </span>
            {away}
          </strong>
          <small>
            {[
              slot.league_name || slot.league_short || "Competition not recorded",
              country ? `${country.flag} ${country.name}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </small>
          <small>
            {slot.kickoff_at ? singlesDate(slot.kickoff_at) : "Kickoff unconfirmed"} · Accra
          </small>
        </div>
        <div className="pb-prem-leg-pickline">
          <small>{market}</small>
          <strong>{pick}</strong>
        </div>
        <div className="singles-price">
          <span className="singles-mobile-label">Recorded odds</span>
          <strong>{fmtOdds(quotedOdds)}</strong>
          {quotedOdds === null && <small>Not quoted</small>}
        </div>
        <span className={`singles-status ${slot.outcome ?? "pending"}`}>
          {selectionState(slot, now)}
        </span>
      </article>
      <SelectionEvidence slot={slot} />
    </div>
  );
}

export function SinglesBoard({
  slots,
  status,
  policyBlocked = false,
  loading,
  error,
  fallback,
  now = Date.now(),
}: {
  slots: readonly ScSlot[];
  status?: ScSession["status"];
  policyBlocked?: boolean;
  loading: boolean;
  error: boolean;
  fallback?: ReactNode;
  now?: number;
}): JSX.Element {
  const [filter, setFilter] = useState<SelectionFilter>("all");
  const settled = slots.filter((slot) => slot.outcome && slot.outcome !== "pending").length;
  const pending = slots.length - settled;
  const visible = slots.filter(
    (slot) =>
      filter === "all" ||
      (filter === "settled"
        ? !!slot.outcome && slot.outcome !== "pending"
        : !slot.outcome || slot.outcome === "pending"),
  );
  return (
    <section
      className="singles-panel singles-board"
      aria-labelledby="singles-board-title"
      aria-busy={loading}
    >
      <div className="singles-panel-header">
        <div>
          <span className="pb-eyebrow">CURRENT PUBLICATION</span>
          <h2 id="singles-board-title">
            Today's selections{" "}
            <span className="singles-count">
              {(loading || error) && !slots.length ? "—" : slots.length}
            </span>
          </h2>
          <p>Exact selections, recorded prices and verified outcomes.</p>
        </div>
        <span className="singles-timezone">ACCRA · GMT</span>
      </div>
      {slots.length > 0 && (
        <div className="singles-board-toolbar">
          <div className="pb-segmented" aria-label="Filter Singles selections">
            {(
              [
                ["all", "All", slots.length],
                ["pending", "Awaiting result", pending],
                ["settled", "Settled", settled],
              ] as const
            ).map(([key, title, count]) => (
              <button
                type="button"
                key={key}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
              >
                {title}
                <span>{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {loading && !slots.length ? (
        <SinglesLoading />
      ) : !slots.length ? (
        <>
          <SinglesEmpty
            title={
              error
                ? "Selection board unavailable"
                : policyBlocked
                  ? "Selection excluded by current policy"
                  : status === "rest_day"
                    ? "A scheduled rest day"
                    : status === "no_fixtures"
                      ? "No qualifying selection"
                      : "Awaiting today's publication"
            }
          >
            {error
              ? "We couldn't load the current board. Refresh to try again."
              : policyBlocked
                ? "No permitted replacement is published for this session. Original records remain preserved."
                : status === "no_fixtures"
                  ? "No selection met this session's publication checks. The next qualified selection will appear here."
                  : status === "rest_day"
                    ? "There is no published selection for this session."
                    : "Your next published selection and its supporting details will appear here."}
          </SinglesEmpty>
          {fallback}
        </>
      ) : visible.length ? (
        <>
          <div className="singles-workspace-columns" aria-hidden="true">
            <span>#</span>
            <span>Fixture / competition</span>
            <span>Market / selection</span>
            <span>Odds</span>
            <span>Outcome</span>
          </div>
          {visible.map((slot) => (
            <SinglesSelectionRow key={slot.id} slot={slot} index={slots.indexOf(slot)} now={now} />
          ))}
          <div className="singles-panel-foot">
            <span>
              {slots.length} original {slots.length === 1 ? "selection" : "selections"} · {settled}{" "}
              settled
            </span>
            <span>Outcomes update after verification</span>
          </div>
        </>
      ) : (
        <SinglesEmpty title="No selections in this view">
          Choose another filter to see the rest of today's board.
        </SinglesEmpty>
      )}
    </section>
  );
}

export function SinglesLoading(): JSX.Element {
  return (
    <div className="singles-loading" role="status" aria-label="Loading Singles data">
      <span className="singles-skeleton" />
      <span className="singles-skeleton" />
      <span className="singles-skeleton" />
      <span className="singles-loading-label">Loading the latest records…</span>
    </div>
  );
}

export function SinglesReserveAside({
  reserve,
  loading,
  error,
  unseeded,
  session,
  onSeed,
  onReserve,
}: {
  reserve: ScReserve | null;
  loading: boolean;
  error: boolean;
  unseeded: boolean;
  session: ScSession | null;
  onSeed: () => void;
  onReserve: () => void;
}): JSX.Element {
  return (
    <aside className="singles-aside">
      <section className="singles-reserve-card">
        <span className="pb-eyebrow">YOUR TRACKED RESERVE</span>
        <div className="singles-reserve-head">
          <h2>{unseeded && !loading && !error ? "Start with a seed" : "Reserve overview"}</h2>
          <Icon name="singles" size={17} />
        </div>
        {loading && !reserve ? (
          <p>Loading reserve…</p>
        ) : error && (!reserve || unseeded) ? (
          <p role="status">Reserve data is unavailable. Your selection board remains separate.</p>
        ) : unseeded ? (
          <>
            <p>
              Record your opening stake to track your chain. Selections remain available before you
              set a seed.
            </p>
            <button
              type="button"
              className="pb-primary-button"
              onClick={onSeed}
              data-testid="sc-set-seed-cta"
            >
              Set opening stake <span aria-hidden="true">↗</span>
            </button>
          </>
        ) : (
          <>
            {error && <p role="status">Refresh unavailable. Showing the last loaded reserve.</p>}
            <div className="singles-reserve-value">
              {singlesMoney(reserve?.total_balance)}
              <small>{session?.simulated ? "Paper reserve" : "Recorded balance"}</small>
            </div>
            <dl>
              <div>
                <dt>Capital</dt>
                <dd>{singlesMoney(reserve?.capital_balance)}</dd>
              </div>
              <div>
                <dt>Profit</dt>
                <dd>{singlesMoney(reserve?.profit_balance)}</dd>
              </div>
              <div>
                <dt>Reserve status</dt>
                <dd>{singlesStatus(reserve?.status)}</dd>
              </div>
            </dl>
            <button type="button" className="pb-primary-button" onClick={onReserve}>
              View reserve history <span aria-hidden="true">↗</span>
            </button>
          </>
        )}
        <small className="singles-reserve-disclaimer">
          A tracking record. No bet is placed from this workspace.
        </small>
      </section>
      <section className="pb-workspace-note singles-workspace-note">
        <h3>Follow each selection.</h3>
        <ol>
          <li>
            <span>01</span>
            <div>
              <strong>Inspect the board</strong>
              <p>See the fixture, exact market and recorded price.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <strong>Read the details</strong>
              <p>Open the source notes, recent results and limitations.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <strong>Follow settlement</strong>
              <p>View verified outcomes and your chain's recorded activity.</p>
            </div>
          </li>
        </ol>
      </section>
    </aside>
  );
}

export function SinglesBankedWins({ chains }: { chains: readonly ScChain[] }): JSX.Element | null {
  const wins = chains.filter(
    (chain) =>
      chain.status === "settled_win" &&
      (chain.g3_return ?? chain.g2_return ?? chain.g1_return ?? 0) > 0,
  );
  if (!wins.length) return null;
  return (
    <section className="singles-panel singles-wins" aria-label="Bank settled wins">
      <div className="singles-panel-header">
        <div>
          <span className="pb-eyebrow">SETTLED CHAINS</span>
          <h2>Bank your wins</h2>
          <p>Choose a settled return to record against a SIKA goal.</p>
        </div>
      </div>
      {wins.map((chain) => {
        const amount = chain.g3_return ?? chain.g2_return ?? chain.g1_return ?? 0;
        return (
          <div className="singles-bank-row" key={chain.id}>
            <div>
              <strong>{chain.chain_label}</strong>
              <small>{singlesDate(chain.created_at)}</small>
            </div>
            <span>{singlesMoney(amount)}</span>
            <BankToGoalCta
              source="singles_chain"
              sourceId={chain.id}
              amount={amount}
              sourceLabel={`Singles ${chain.chain_label}`}
            />
          </div>
        );
      })}
    </section>
  );
}

export function SinglesActivity({
  chains,
  loading,
  error,
}: {
  chains: readonly ScChain[];
  loading: boolean;
  error: boolean;
}): JSX.Element {
  return (
    <>
      <section
        className="singles-panel"
        aria-labelledby="singles-activity-title"
        aria-busy={loading}
      >
        <div className="singles-panel-header">
          <div>
            <span className="pb-eyebrow">YOUR CHAIN RECORD</span>
            <h2 id="singles-activity-title">Chain activity</h2>
            <p>Recorded stakes, returns and outcomes. Dates shown in Accra time.</p>
          </div>
          <span className="singles-count">
            {(loading || error) && !chains.length ? "—" : chains.length}
          </span>
        </div>
        {loading && !chains.length ? (
          <SinglesLoading />
        ) : !chains.length ? (
          <SinglesEmpty title={error ? "Chain activity unavailable" : "No chains recorded yet"}>
            {error
              ? "Refresh to try loading your chain history again."
              : "Your personal chain records will appear here when available."}
          </SinglesEmpty>
        ) : (
          <div className="singles-table-scroll">
            <table className="singles-table">
              <thead>
                <tr>
                  <th>Chain / created</th>
                  <th>Opening stake</th>
                  <th>Last recorded return</th>
                  <th>Day profit</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {chains.map((chain) => (
                  <tr key={chain.id}>
                    <td>
                      <strong>{chain.chain_label}</strong>
                      <small>{singlesDate(chain.created_at)}</small>
                      <details className="singles-chain-details">
                        <summary>View stages</summary>
                        {[
                          chain.g1_stake,
                          chain.g1_return,
                          chain.g2_stake,
                          chain.g2_return,
                          chain.g3_stake,
                          chain.g3_return,
                        ].every((value) => value === null) && <p>No stage amounts recorded yet.</p>}
                        <dl>
                          {([1, 2, 3] as const).map((stage) => {
                            const stake = chain[`g${stage}_stake`];
                            const amount = chain[`g${stage}_return`];
                            return stake === null && amount === null ? null : (
                              <div key={stage}>
                                <dt>Stage {stage}</dt>
                                <dd>
                                  {singlesMoney(stake)} stake · {singlesMoney(amount)} return
                                </dd>
                              </div>
                            );
                          })}
                          {chain.bust_at_game !== null && (
                            <div>
                              <dt>Busted at</dt>
                              <dd>Stage {chain.bust_at_game}</dd>
                            </div>
                          )}
                          {chain.cooldown_ends_at && (
                            <div>
                              <dt>Cooldown ends</dt>
                              <dd>{singlesDate(chain.cooldown_ends_at)}</dd>
                            </div>
                          )}
                        </dl>
                      </details>
                    </td>
                    <td>{singlesMoney(chain.opening_stake)}</td>
                    <td>{singlesMoney(chain.g3_return ?? chain.g2_return ?? chain.g1_return)}</td>
                    <td>{singlesMoney(chain.day_profit)}</td>
                    <td>
                      <span className={`singles-status ${chain.status}`}>
                        {singlesStatus(chain.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="singles-panel-foot">
          <span>
            {error && !chains.length
              ? "Chain history unavailable"
              : `Latest ${chains.length} ${chains.length === 1 ? "chain" : "chains"} returned by your account`}
          </span>
          <span>Records are separate from today's board</span>
        </div>
      </section>
      <SinglesBankedWins chains={chains} />
    </>
  );
}

export function SinglesReserveHistory({
  reserve,
  transactions,
  loading,
  error,
  unseeded,
  onSeed,
}: {
  reserve: ScReserve | null;
  transactions: readonly ScTransaction[];
  loading: boolean;
  error: boolean;
  unseeded: boolean;
  onSeed: () => void;
}): JSX.Element {
  return (
    <section className="singles-panel" aria-labelledby="singles-reserve-title" aria-busy={loading}>
      <div className="singles-panel-header">
        <div>
          <span className="pb-eyebrow">YOUR TRACKING LEDGER</span>
          <h2 id="singles-reserve-title">Reserve &amp; history</h2>
          <p>Your recorded capital, profit and recent transactions.</p>
        </div>
        {!error && !loading && unseeded && (
          <button type="button" className="pb-secondary-button" onClick={onSeed}>
            Set opening stake
          </button>
        )}
      </div>
      {loading && !reserve ? (
        <SinglesLoading />
      ) : error && !reserve ? (
        <SinglesEmpty title="Reserve unavailable">
          Refresh to try loading your reserve again.
        </SinglesEmpty>
      ) : (
        <>
          <dl className="singles-balance-grid">
            <div>
              <dt>Recorded balance</dt>
              <dd>{singlesMoney(reserve?.total_balance)}</dd>
            </div>
            <div>
              <dt>Capital</dt>
              <dd>{singlesMoney(reserve?.capital_balance)}</dd>
            </div>
            <div>
              <dt>Profit</dt>
              <dd>{singlesMoney(reserve?.profit_balance)}</dd>
            </div>
            <div>
              <dt>Recorded rollover</dt>
              <dd>
                {reserve && Number.isFinite(reserve.rollover_pct)
                  ? `${(reserve.rollover_pct * 100).toLocaleString("en-GB", { maximumFractionDigits: 2 })}%`
                  : "—"}
              </dd>
            </div>
          </dl>
          {transactions.length ? (
            <div className="singles-table-scroll">
              <table className="singles-table">
                <caption>Recent reserve transactions</caption>
                <thead>
                  <tr>
                    <th>Transaction / date</th>
                    <th>Amount</th>
                    <th>Capital after</th>
                    <th>Profit after</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>
                        <strong>{tx.tx_type.replaceAll("_", " ")}</strong>
                        <small>{singlesDate(tx.created_at)}</small>
                        {tx.note && <p>{tx.note}</p>}
                      </td>
                      <td>{singlesMoney(tx.amount)}</td>
                      <td>{singlesMoney(tx.capital_after)}</td>
                      <td>{singlesMoney(tx.profit_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <SinglesEmpty title="No reserve transactions">
              {unseeded
                ? "Set an opening stake when you're ready to begin personal tracking."
                : "New reserve activity will appear here after it is recorded."}
            </SinglesEmpty>
          )}
        </>
      )}
    </section>
  );
}
